// src/components/circles/useCircleOwnerCandidates.ts
//
// Owner-picker source for Circle create/edit. Reuses the canonical workspace
// roster every other CRM people-picker reads (tasks assignee/watchers,
// compliance, telephony, provider assignments): GET /api/team, which lists the
// `user_workspace_roles` projection of the ACTIVE workspace. That is the same
// table the server validates an owner against (circlesService.assertValidOwner
// → 400 INVALID_OWNER), so the picker can only offer values the server will
// accept.
//
// /api/team is gated by the Central `team.read` capability. A caller without it
// gets `unavailable`: the form still SHOWS the owner (by label, never by raw id)
// but cannot change it.

import { useMemo } from 'react';
import { useGetTeamMembersQuery } from '../../api/teamApi';
import type { CircleOwnerCandidate, OwnerCandidatesStatus } from './CircleFormFields';

export function useCircleOwnerCandidates(workspaceId: string | undefined): {
  members: CircleOwnerCandidate[];
  status: OwnerCandidatesStatus;
} {
  const { data, isLoading, isFetching, isError } = useGetTeamMembersQuery(
    { workspaceId },
    { skip: !workspaceId },
  );
  const members = useMemo<CircleOwnerCandidate[]>(
    () =>
      (data ?? [])
        // Defence in depth: the roster is already active-workspace scoped
        // server-side; never offer a row from another workspace.
        .filter((m) => !workspaceId || !m.workspaceId || m.workspaceId === workspaceId)
        .map((m) => ({ id: m.userId, fullName: m.fullName, email: m.email })),
    [data, workspaceId],
  );
  const status: OwnerCandidatesStatus = isError
    ? 'unavailable'
    : isLoading || (isFetching && !data) || !workspaceId
      ? 'loading'
      : 'ready';
  return { members, status };
}
